
import { motion } from 'framer-motion';

// Shared container
const VisualContainer = ({ children }: { children: React.ReactNode }) => (
    <div className="w-full aspect-[4/3] bg-gradient-to-br from-white to-blue-50/50 rounded-xl border border-blue-100 shadow-sm overflow-hidden relative flex items-center justify-center p-3 sm:p-6 md:p-8">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#3b82f60a_1px,transparent_1px),linear-gradient(to_bottom,#3b82f60a_1px,transparent_1px)] bg-[size:16px_16px] [mask-image:radial-gradient(ellipse_at_center,black_60%,transparent_100%)] pointer-events-none" />
        {children}
    </div>
);

// Component 1: Realistic Simulations (Investor Bot Chat)
export const RehearsalVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] h-[200px] sm:h-[320px] bg-white rounded-lg shadow-md border border-gray-100 p-3 sm:p-6 flex flex-col relative overflow-hidden">

                {/* Header */}
                <div className="flex items-center gap-3 border-b border-gray-100 pb-3 mb-4">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9v-2h2v2zm0-4H9V7h2v5z" /></svg>
                    </div>
                    <div>
                        <div className="text-xs font-bold text-gray-800">VC Simulator</div>
                        <div className="text-[10px] text-green-500 font-bold flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Online
                        </div>
                    </div>
                </div>

                {/* Chat Area */}
                <div className="flex-1 space-y-4">
                    {/* Bot Message */}
                    <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        className="bg-gray-100 rounded-tr-xl rounded-b-xl p-3 max-w-[85%]"
                    >
                        <div className="text-[10px] text-gray-500 font-bold mb-1">Question 1/5</div>
                        <div className="text-[11px] text-gray-700 leading-relaxed">
                            How do you plan to scale your customer acquisition cost (CAC) as you expand into enterprise markets?
                        </div>
                    </motion.div>

                    {/* User Reply (Simulated) */}
                    <motion.div
                        initial={{ opacity: 0, x: 10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 1 }}
                        className="bg-blue-50 rounded-tl-xl rounded-b-xl p-3 max-w-[85%] self-end ml-auto border border-blue-100"
                    >
                        <div className="text-[11px] text-blue-900 leading-relaxed">
                            We're implementing a PLG motion that will lower CAC by...
                        </div>
                        {/* Typing Indicator */}
                        <div className="flex gap-1 mt-1">
                            <motion.div className="w-1 h-1 bg-blue-400 rounded-full" animate={{ y: [0, -2, 0] }} transition={{ repeat: Infinity, duration: 0.6 }} />
                            <motion.div className="w-1 h-1 bg-blue-400 rounded-full" animate={{ y: [0, -2, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }} />
                            <motion.div className="w-1 h-1 bg-blue-400 rounded-full" animate={{ y: [0, -2, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.4 }} />
                        </div>
                    </motion.div>
                </div>

                {/* Microphone/Input (Visual Only) */}
                <div className="mt-4 flex gap-2">
                    <div className="flex-1 h-8 bg-gray-50 rounded-full border border-gray-200" />
                    <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-sm">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" /><path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" /></svg>
                    </div>
                </div>

            </div>
        </VisualContainer>
    );
};

// Component 2: Constructive Feedback (Analytics Card)
export const FeedbackVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] h-[200px] sm:h-[320px] bg-white rounded-lg shadow-md border border-gray-100 p-3 sm:p-6 flex flex-col relative overflow-hidden">

                {/* Score Header */}
                <div className="flex justify-between items-start mb-3 sm:mb-6">
                    <div>
                        <div className="text-sm font-bold text-gray-900">Answer Analysis</div>
                        <div className="text-[10px] text-gray-500">Based on VC benchmarks</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-emerald-500 font-syne">92<span className="text-sm text-gray-400 font-sans font-normal">/100</span></div>
                        <div className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1 rounded">STRONG</div>
                    </div>
                </div>

                {/* Feedback Metrics */}
                <div className="space-y-4">
                    {[
                        { label: 'Clarity', score: 95, color: 'bg-emerald-500' },
                        { label: 'Confidence', score: 88, color: 'bg-emerald-500' },
                        { label: 'Data Support', score: 70, color: 'bg-yellow-500', warn: true }
                    ].map((metric, i) => (
                        <div key={i}>
                            <div className="flex justify-between text-[10px] mb-1">
                                <span className="font-bold text-gray-700">{metric.label}</span>
                                {metric.warn && <span className="text-yellow-600 flex items-center gap-1 font-bold">Needs Evidence <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg></span>}
                            </div>
                            <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                                <motion.div
                                    className={`h-full ${metric.color}`}
                                    initial={{ width: 0 }}
                                    whileInView={{ width: `${metric.score}%` }}
                                    transition={{ duration: 1, delay: i * 0.2 }}
                                />
                            </div>
                        </div>
                    ))}
                </div>

                {/* AI Tip Box */}
                <motion.div
                    className="mt-6 p-3 bg-blue-50 border border-blue-100 rounded-lg flex gap-3 items-start"
                    initial={{ y: 20, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1 }}
                >
                    <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center shrink-0 text-blue-600 text-[10px]">✨</div>
                    <div className="text-[10px] text-blue-800 leading-tight">
                        <strong>Tip:</strong> Mention specific competitors when discussing market entry to show deeper awareness.
                    </div>
                </motion.div>

            </div>
        </VisualContainer>
    );
};
